"use client";

import { useId, useState } from "react";

import type { Classification } from "../_lib/types";

// Visual treatment per spec FR-005/FR-006. Hover OR tap reveals the
// editorial rationale (FR-006: the explainer is accessible from the
// badge). Colour is editorial-neutral — it encodes the class, not a
// value judgement on the variance.
const STYLE: Record<Classification, { label: string; cls: string }> = {
  structural: {
    label: "structural",
    cls: "bg-signal/10 text-signal border-signal/30",
  },
  "one-time": {
    label: "one-time",
    cls: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  },
  mixed: {
    label: "mixed",
    cls: "bg-violet-500/10 text-violet-700 border-violet-500/30",
  },
  unclassified: {
    label: "unclassified",
    cls: "bg-ink-3/10 text-ink-3 border-ink-3/30",
  },
};

type Props = {
  classification: Classification;
  rationale: string;
};

export function ClassificationBadge({ classification, rationale }: Props) {
  const [open, setOpen] = useState(false);
  const tipId = useId();
  const s = STYLE[classification];

  return (
    <span className="relative inline-block">
      <button
        type="button"
        aria-describedby={open ? tipId : undefined}
        aria-expanded={open}
        // Hover only on a real mouse: a touch tap synthesizes
        // pointerenter→click, which (with a plain onMouseEnter) would open
        // then the toggle would immediately close it, so the first tap
        // never revealed the rationale. Gating hover to pointerType
        // "mouse" lets tap fall through to onClick as a clean toggle.
        onPointerEnter={(e) => {
          if (e.pointerType === "mouse") setOpen(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") setOpen(false);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
        className={`cs-mono inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] uppercase tracking-wider transition-colors ${s.cls}`}
      >
        {s.label}
      </button>
      {open ? (
        <span
          id={tipId}
          role="tooltip"
          className="absolute left-0 top-full z-10 mt-1 block w-72 rounded border border-rule bg-paper p-3 text-left text-xs leading-snug text-ink-2 shadow-lg"
        >
          {rationale}
        </span>
      ) : null}
    </span>
  );
}
